import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useCrmMessaging } from "./useCrmMessaging";
import type { CrmEmbedProps, CrmEmbedRef } from "./types";
import { cn } from "@/lib/utils";
import { useAuthStore, ensureFreshToken } from "@/lib/store/authStore";
import { AnimatedStarburst } from "@/components/icons/AnimatedStarburst";

const INITIAL_LOAD_TIMEOUT_MS = 30000; // 30 seconds for initial load

export const CrmEmbed = forwardRef<CrmEmbedRef, CrmEmbedProps>(
  function CrmEmbed(props, ref) {
    const {
      initialPath = "",
      className,
      onReady,
      onRouteChange,
      onError,
      onNavigateParent,
    } = props;

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    const messaging = useCrmMessaging(iframeRef, {
      onReady,
      onRouteChange,
      onError,
      onNavigateParent,
    });

    // Timeout for initial load
    useEffect(() => {
      if (messaging.isReady) return;

      const timeout = setTimeout(() => {
        if (!messaging.isReady) {
          setLoadError("CRM failed to load. Please refresh the page.");
          onError?.("CRM load timeout");
        }
      }, INITIAL_LOAD_TIMEOUT_MS);

      return () => clearTimeout(timeout);
    }, [messaging.isReady, onError]);

    // Expose ref API to parent
    useImperativeHandle(
      ref,
      () => ({
        navigate: messaging.navigate,
        getState: messaging.getState,
        isReady: messaging.isReady,
      }),
      [messaging.navigate, messaging.getState, messaging.isReady]
    );

    // Get auth mode and tenant server URL from store
    const authMode = useAuthStore((state) => state.authMode);
    const tenantServerUrl = useAuthStore((state) => state.tenantServerUrl);
    const cpTokenFromStore = useAuthStore((state) => state.cpToken);

    // Build iframe URL/srcdoc — awaits fresh token to avoid 401 race condition.
    const [iframeSrc, setIframeSrc] = useState<string | undefined>(undefined);
    const [iframeSrcDoc, setIframeSrcDoc] = useState<string | undefined>(undefined);

    useEffect(() => {
      let cancelled = false;

      (async () => {
        let baseUrl: string;
        if (authMode === 'control_plane' && tenantServerUrl) {
          baseUrl = tenantServerUrl.replace(/\/$/, '');
        } else {
          baseUrl = import.meta.env.VITE_FRAPPE_URL ??
            (window.location.port === "5173"
              ? `${window.location.protocol}//${window.location.hostname}:8000`
              : window.location.origin);
        }

        // Normalize path
        let path = initialPath;
        if (path && !path.startsWith("/")) path = `/${path}`;
        if (path.endsWith("/") && path.length > 1) path = path.slice(0, -1);

        const targetUrl = `/crm${path}?embed=true`;

        if (authMode === 'control_plane' && tenantServerUrl) {
          const freshToken = await ensureFreshToken();
          if (cancelled) return;

          if (freshToken) {
            const ssoEndpoint = `${baseUrl}/api/method/tenant_provisioning.auth.sso_api.iframe_sso_post`;
            const html = `<!DOCTYPE html>
<html>
<head><title>Loading...</title></head>
<body>
<form id="sso" method="POST" action="${ssoEndpoint}">
<input type="hidden" name="token" value="${freshToken}" />
<input type="hidden" name="redirect_to" value="${targetUrl}" />
</form>
<script>document.getElementById('sso').submit();</script>
</body>
</html>`;
            setIframeSrcDoc(html);
            setIframeSrc(undefined);
            return;
          }
        }

        setIframeSrc(`${baseUrl}${targetUrl}`);
        setIframeSrcDoc(undefined);
      })();

      return () => { cancelled = true; };
    }, [initialPath, authMode, tenantServerUrl, cpTokenFromStore]);

    // Handle iframe load error
    const handleIframeError = useCallback(() => {
      setLoadError("Failed to load CRM. Please check your connection.");
      onError?.("iframe load error");
    }, [onError]);

    // Error state
    if (loadError) {
      return (
        <div
          className={cn(
            "crm-embed-wrapper relative w-full h-full min-h-[400px] bg-white flex items-center justify-center",
            className
          )}
        >
          <div className="flex flex-col items-center gap-4 text-center p-6">
            <div className="text-destructive font-medium">{loadError}</div>
            <button
              onClick={() => {
                setLoadError(null);
                messaging.reset();
              }}
              className="text-sm text-blue-600 hover:underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "crm-embed-wrapper relative w-full h-full min-h-[400px] bg-white",
          className
        )}
      >
        {/* Loading overlay */}
        {!messaging.isReady && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-gray-500 z-10 bg-white">
            <AnimatedStarburst size={48} />
            <span className="text-sm">Loading CRM...</span>
          </div>
        )}

        {/* CRM iframe */}
        <iframe
          ref={iframeRef}
          {...(iframeSrcDoc ? { srcDoc: iframeSrcDoc } : { src: iframeSrc })}
          className={cn(
            "absolute inset-0 w-full h-full border-none transition-opacity duration-200",
            messaging.isReady ? "opacity-100" : "opacity-0"
          )}
          title="CRM"
          allow="clipboard-read; clipboard-write"
          onError={handleIframeError}
        />
      </div>
    );
  }
);

CrmEmbed.displayName = "CrmEmbed";
